import React, { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { formatDayLabel } from '../../photoUpload/dayBuckets';
import { useDayPhotos } from '../../hooks/useProjectPhotos';

// Cap the visible thumb tiles per day. If the day holds more, we render a
// "+ N" tile so a 500-photo day doesn't blow up the DOM. Full-day view is
// out of scope for this phase (there's no dedicated day route yet).
const THUMBS_PER_DAY = 7;

function PhotoThumb({ photo }) {
  const [url, setUrl] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const path = photo.thumbPath || photo.storagePath;
    if (!path) return () => {};
    getDownloadURL(ref(storage, path))
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setErrored(true); });
    return () => { cancelled = true; };
  }, [photo.thumbPath, photo.storagePath]);

  if (errored || !url) {
    return <div className="pi-thumb pi-thumb--placeholder" />;
  }
  return (
    <div
      className="pi-thumb"
      style={{ backgroundImage: `url(${url})` }}
      role="img"
      aria-label={photo.fileName || 'photo'}
    />
  );
}

const PhotoDaySection = ({
  projectId,
  dayKey,
  photoCount,
  expanded,
  onToggle,
  selectable = false,
  selected = false,
  onToggleSelected,
}) => {
  const { photos, loading } = useDayPhotos(projectId, dayKey, { enabled: expanded });
  const shownPhotos = expanded && photos ? photos.slice(0, THUMBS_PER_DAY) : [];
  const overflow = expanded && photos && photos.length > THUMBS_PER_DAY
    ? photos.length - THUMBS_PER_DAY
    : Math.max(0, (photoCount || 0) - THUMBS_PER_DAY);

  return (
    <div
      className="pi-day-section"
      data-testid="photo-day-section"
      data-day={dayKey}
      data-selected={selectable ? String(selected) : undefined}
    >
      {selectable && (
        // A separate control, because the header click already means
        // expand/collapse. Shift-click extends from the last day touched —
        // the one-gesture path for a long stretch of days.
        <label className="pi-day-select">
          <input
            type="checkbox"
            checked={selected}
            data-testid="photo-day-checkbox"
            aria-label={`Include ${formatDayLabel(dayKey)} in the timelapse`}
            onChange={() => {}}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelected(dayKey, e.shiftKey);
            }}
          />
        </label>
      )}
      <button
        type="button"
        className="pi-day-head"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="pi-day-title">{formatDayLabel(dayKey)}</span>
        <span className="pi-day-meta">
          {(photoCount || 0).toLocaleString()} photo{photoCount === 1 ? '' : 's'}
        </span>
        <span className="pi-day-chevron">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="pi-thumb-grid">
          {loading && !photos && (
            <div className="pi-thumb pi-thumb--placeholder" />
          )}
          {shownPhotos.map((p) => (
            <PhotoThumb key={p.id} photo={p} />
          ))}
          {overflow > 0 && (
            <div className="pi-thumb pi-thumb--more">+ {overflow}</div>
          )}
        </div>
      )}
    </div>
  );
};

// Coverage.days is a map keyed by 'YYYY-MM-DD' | 'unknown', each entry
// { photoCount }. Coverage totals come from the same doc. If a project has
// zero photos we render nothing.
const PhotoDayGrid = ({
  projectId,
  coverage,
  // Day selection (#385). When `selectable`, each day gets a checkbox and the
  // chosen set scopes the next compile. State lives in FootageTab because the
  // compile CTA is a sibling that has to cost and send the same choice.
  selectable = false,
  selectedDays = null,
  onToggleDay = null,
}) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const days = coverage?.days || {};
  const dayKeys = Object.keys(days).filter((k) => (days[k]?.photoCount || 0) > 0);
  if (dayKeys.length === 0) return null;

  const sortedKeys = [...dayKeys].sort((a, b) => {
    if (a === 'unknown') return 1;
    if (b === 'unknown') return -1;
    return a < b ? 1 : a > b ? -1 : 0;
  });

  const toggle = (dayKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey); else next.add(dayKey);
      return next;
    });
  };

  return (
    <div className="pi-day-grid" data-selectable={String(selectable)}>
      {sortedKeys.map((k) => (
        <PhotoDaySection
          key={k}
          projectId={projectId}
          dayKey={k}
          photoCount={days[k]?.photoCount}
          expanded={expanded.has(k)}
          onToggle={() => toggle(k)}
          selectable={selectable}
          selected={selectable ? !!selectedDays?.has(k) : false}
          onToggleSelected={onToggleDay}
        />
      ))}
    </div>
  );
};

export default PhotoDayGrid;
